import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO head helper. Renders page title, meta description, canonical URL,
 * Open Graph + Twitter cards and optional JSON-LD structured data.
 *
 * Props:
 *  - title: page title (full, includes brand suffix if desired)
 *  - description: meta description (max 160 chars)
 *  - path: route path (e.g. "/servizi/siti-web") for canonical URL
 *  - image: absolute OG image URL (defaults to brand logo)
 *  - jsonLd: object OR array of objects (Schema.org) to inject
 *  - noindex: true to add robots noindex,nofollow
 */
const SITE_URL = 'https://digitaltechsolutions.it';
const DEFAULT_IMAGE = `${SITE_URL}/dts-logo.png`;

export default function SEOHead({
  title,
  description,
  path = '',
  image = DEFAULT_IMAGE,
  jsonLd = null,
  noindex = false,
}) {
  const canonical = `${SITE_URL}${path.startsWith('/') ? path : '/' + path}`.replace(/\/$/, '') || SITE_URL;
  const safeDesc = (description || '').slice(0, 160);
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={safeDesc} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow,max-image-preview:large" />}

      {/* Open Graph */}
      <meta property="og:site_name" content="Digital Tech Solutions" />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="it_IT" />
      <meta property="og:url" content={canonical} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={safeDesc} />}
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={safeDesc} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * Canonical Organization + LocalBusiness block. Reused in index.html and Home.
 */
export const ORGANIZATION_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Digital Tech Solutions',
  alternateName: 'DTS',
  url: SITE_URL,
  logo: `${SITE_URL}/dts-logo.png`,
  email: 'info@digitaltechsolutions.it',
  telephone: '+39 377 366 0476',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Via Pietro Nenni, 16',
    addressLocality: 'Modica',
    addressRegion: 'RG',
    postalCode: '97015',
    addressCountry: 'IT',
  },
  sameAs: [],
};

export const LOCAL_BUSINESS_LD = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}#localbusiness`,
  name: 'Digital Tech Solutions',
  image: `${SITE_URL}/dts-logo.png`,
  url: SITE_URL,
  telephone: '+39 377 366 0476',
  email: 'info@digitaltechsolutions.it',
  priceRange: '€€',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Via Pietro Nenni, 16',
    addressLocality: 'Modica',
    addressRegion: 'RG',
    postalCode: '97015',
    addressCountry: 'IT',
  },
  areaServed: [
    { '@type': 'City', name: 'Modica' },
    { '@type': 'City', name: 'Ragusa' },
    { '@type': 'City', name: 'Siracusa' },
    { '@type': 'City', name: 'Catania' },
    { '@type': 'AdministrativeArea', name: 'Sicilia' },
    { '@type': 'Country', name: 'Italia' },
  ],
};

export const buildServiceLd = ({ name, description, slug }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: name,
  provider: { '@type': 'Organization', name: 'Digital Tech Solutions', url: SITE_URL },
  areaServed: [
    { '@type': 'City', name: 'Modica' },
    { '@type': 'City', name: 'Ragusa' },
    { '@type': 'City', name: 'Siracusa' },
    { '@type': 'City', name: 'Catania' },
  ],
  description,
  url: `${SITE_URL}/servizi/${slug}`,
});
